import { useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";

import { ProjectAccessChip } from "@/components/common/project-access-chip/project-access-chip";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useAddIssueContributor } from "@/hooks/use-issue-mutations";
import { useProject } from "@/hooks/use-project";
import { ApiError, OfflineError, apiErrorMessage } from "@/lib/api";
import { personInitials, personName } from "@/lib/issues";
import { memberAccess } from "@/lib/project-access";
import type { ProjectUser } from "@/types/api";
import { pickableMembers, type PickerIssue } from "./pickable-members";

interface ContributorPickerProps {
  projectId: string;
  issue: PickerIssue;
  currentUserId: string | undefined;
  /** Someone was added: the host closes. The toast is already up. */
  onAdded: () => void;
}

function matches(person: ProjectUser, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return (
    personName(person).toLowerCase().includes(needle) ||
    person.email.toLowerCase().includes(needle)
  );
}

function addErrorMessage(err: Error): string {
  if (err instanceof OfflineError) return "You're offline. Connect to add people.";
  if (err instanceof ApiError && err.status === 403) {
    return "You can't add people to this issue any more.";
  }
  // The server's own sentence: they've left the project, or are already on
  // the issue as its raiser or assignee.
  if (err instanceof ApiError && err.status === 400) {
    return apiErrorMessage(err, "They can't be added to this issue.");
  }
  return "Couldn't add them. Try again.";
}

/**
 * Add someone already on the project to an issue in one tap. Lives inside the
 * Add contributor sheet, which owns the header and the back arrow. See
 * [[contributor-picker.spec]].
 */
export function ContributorPicker({
  projectId,
  issue,
  currentUserId,
  onAdded,
}: ContributorPickerProps) {
  const { data: project, isLoading } = useProject(projectId);
  const addContributor = useAddIssueContributor();
  const [query, setQuery] = useState("");
  const [addingId, setAddingId] = useState<string | null>(null);

  const people = useMemo(
    () => (project ? pickableMembers(project, issue, currentUserId) : []),
    [project, issue, currentUserId],
  );
  const shown = people.filter((person) => matches(person, query));

  // No confirmation: Remove in Contributors undoes an add, and a confirm would
  // double the taps for the common case.
  const add = (person: ProjectUser) => {
    if (addContributor.isPending) return;
    setAddingId(person.id);
    addContributor.mutate(
      { projectId, issueId: issue.id, userId: person.id },
      {
        onSuccess: () => {
          toast(`${personName(person)} added`);
          onAdded();
        },
        onError: (err) => {
          toast(addErrorMessage(err));
          if (
            !(err instanceof OfflineError) &&
            !(err instanceof ApiError && err.status < 500)
          ) {
            Sentry.captureMessage("Add issue contributor failed", {
              level: "error",
              tags: { area: "issue-contributors" },
              extra: {
                projectId,
                issueId: issue.id,
                errorName: err.name,
                status: err instanceof ApiError ? err.status : undefined,
              },
            });
          }
        },
        onSettled: () => setAddingId(null),
      },
    );
  };

  const note = (
    <p className="px-0.5 text-[13px] leading-snug text-muted-foreground">
      Otus won&apos;t notify them. Let them know yourself.
    </p>
  );

  if (!project) {
    return (
      <div className="flex flex-col gap-2.5 px-4 pb-4">
        {isLoading &&
          [0, 1, 2].map((key) => (
            <Skeleton key={key} className="h-14 w-full rounded-xl" />
          ))}
      </div>
    );
  }

  if (people.length === 0) {
    return (
      <div className="flex flex-col gap-2.5 px-4 pb-4">
        {note}
        <p className="py-6 text-center text-sm text-muted-foreground">
          Everyone on this project is already on this issue.
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2.5 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div className="relative shrink-0">
        <Search
          className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search people"
          aria-label="Search people"
          className="h-11 pl-9"
        />
      </div>
      {note}
      {shown.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No one matches “{query.trim()}”.
        </p>
      ) : (
        <ul className="min-h-0 overflow-y-auto rounded-xl border border-border bg-card">
          {shown.map((person) => {
            const access = memberAccess(project, person.id);
            const adding = addingId === person.id;
            return (
              <li key={person.id} className="border-b border-border last:border-b-0">
                <button
                  type="button"
                  onClick={() => add(person)}
                  aria-disabled={addContributor.isPending}
                  className="flex min-h-14 w-full items-center gap-3 py-2.5 pr-3.5 pl-3 text-left transition-colors hover:bg-accent"
                >
                  <span
                    aria-hidden="true"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground"
                  >
                    {personInitials(person)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">
                    {personName(person)}
                  </span>
                  {adding ? (
                    <Loader2
                      className="h-4 w-4 shrink-0 animate-spin text-muted-foreground"
                      aria-label="Adding"
                    />
                  ) : (
                    access && <ProjectAccessChip access={access} className="shrink-0" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
